import { useState, useCallback, useEffect } from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Eye,
  Save,
  ChevronLeft,
  Plus,
  GripVertical,
  X,
  Undo,
  Redo,
  Monitor,
  Smartphone,
  ExternalLink,
} from "lucide-react";

import { TEMPLATE_CONFIGS, AVAILABLE_SECTIONS, type TemplateType } from "../../../config/templateConfig";
import SortableSection from "./SortableSection";
import SectionEditor from "./SectionEditor";
import AddSectionModal from "./AddSectionModal";
import LivePreview from "./LivePreview";
import { axiosInstance } from "../../../lib/axios";
import toast from "react-hot-toast";
import { RotateCcw } from "lucide-react";

interface Props {
  initialData: any;
  templateId: TemplateType;
  onSave: (data: any) => void;
  onBack: () => void;
  isSaving: boolean;
}

// Flatten a section's structured content into readable text for the
// before/after comparison panes.
const flatText = (v: any): string => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(flatText).filter(Boolean).join("\n");
  if (typeof v === "object")
    return Object.entries(v)
      .map(([k, val]) => {
        const t = flatText(val);
        return t ? `${k}: ${t}` : "";
      })
      .filter(Boolean)
      .join("\n");
  return String(v);
};

const ProposalBuilderEditor = ({
  initialData,
  templateId,
  onSave,
  onBack,
  isSaving,
}: Props) => {
  const [proposal, setProposal] = useState(initialData);
  const [activeSection, setActiveSection] = useState<string | null>(
    initialData.sections?.[0]?.id || null
  );
  const [draggedSection, setDraggedSection] = useState<any>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [history, setHistory] = useState<any[]>([initialData]);
  const [historyIndex, setHistoryIndex] = useState(0);
  // Per-section AI enhance state: which section is enhancing + the pre-enhance
  // content so the BD can compare (before/after) and revert.
  const [enhancingId, setEnhancingId] = useState<string | null>(null);
  const [revertMap, setRevertMap] = useState<Record<string, any>>({});
  // Side-by-side BD Input vs AI Enhanced comparison.
  const [compare, setCompare] = useState<{ sectionId: string; before: any; after: any } | null>(null);
  // Upwork accounts + clients for tagging which account/client this proposal belongs to.
  const [accounts, setAccounts] = useState<{ _id: string; name: string }[]>([]);
  const [clients, setClients] = useState<{ _id: string; name: string }[]>([]);
  useEffect(() => {
    axiosInstance.get("/bids/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    axiosInstance.get("/clients").then((r) => setClients(r.data?.clients || r.data || [])).catch(() => {});
  }, []);

  const templateConfig = TEMPLATE_CONFIGS[templateId];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const section = proposal.sections.find((s: any) => s.id === active.id);
    setDraggedSection(section);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = proposal.sections.findIndex((s: any) => s.id === active.id);
      const newIndex = proposal.sections.findIndex((s: any) => s.id === over.id);
      const newSections = arrayMove(proposal.sections, oldIndex, newIndex).map(
        (section: any, index: number) => ({ ...section, order: index + 1 })
      );
      updateProposal({ sections: newSections });
    }
    setDraggedSection(null);
  };

  const updateProposal = useCallback((updates: Partial<typeof proposal>) => {
    const newProposal = { ...proposal, ...updates };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newProposal);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setProposal(newProposal);
  }, [proposal, history, historyIndex]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setProposal(history[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setProposal(history[historyIndex + 1]);
    }
  };

  const updateSection = (sectionId: string, updates: any) => {
    const newSections = proposal.sections.map((s: any) =>
      s.id === sectionId ? { ...s, ...updates } : s
    );
    updateProposal({ sections: newSections });
  };

  // AI-enhance one section: improve the BD's wording, keep the structure. Stores
  // the original so the BD can compare and revert; the BD can still edit after.
  const enhanceSection = async (sectionId: string) => {
    const section = proposal.sections.find((s: any) => s.id === sectionId);
    if (!section) return;
    setEnhancingId(sectionId);
    try {
      const { data } = await axiosInstance.post("/proposals/section-enhance", { section });
      if (!data.aiGenerated) { toast("AI unavailable — section unchanged.", { icon: "ℹ️" }); return; }
      const before = { title: section.title, subtitle: section.subtitle, content: section.content };
      setRevertMap((m) => ({ ...m, [sectionId]: before }));
      updateSection(sectionId, { title: data.after.title, subtitle: data.after.subtitle, content: data.after.content });
      setCompare({ sectionId, before, after: data.after });
      toast.success("Section enhanced with AI — review the comparison, you can still edit it.");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to enhance section");
    } finally {
      setEnhancingId(null);
    }
  };

  const revertSection = (sectionId: string) => {
    const before = revertMap[sectionId];
    if (!before) return;
    updateSection(sectionId, before);
    setRevertMap((m) => { const n = { ...m }; delete n[sectionId]; return n; });
    toast("Reverted to your original content.", { icon: "↩️" });
  };

  const deleteSection = (sectionId: string) => {
    const newSections = proposal.sections
      .filter((s: any) => s.id !== sectionId)
      .map((s: any, index: number) => ({ ...s, order: index + 1 }));
    updateProposal({ sections: newSections });
    if (activeSection === sectionId) {
      setActiveSection(newSections[0]?.id || null);
    }
  };

  const duplicateSection = (sectionId: string) => {
    const sectionIndex = proposal.sections.findIndex((s: any) => s.id === sectionId);
    const section = proposal.sections[sectionIndex];
    const newSection = {
      ...section,
      id: `${section.id}-${Date.now()}`,
      title: `${section.title} (Copy)`,
    };
    const newSections = [
      ...proposal.sections.slice(0, sectionIndex + 1),
      newSection,
      ...proposal.sections.slice(sectionIndex + 1),
    ].map((s, index) => ({ ...s, order: index + 1 }));
    updateProposal({ sections: newSections });
    setActiveSection(newSection.id);
  };

  const addSection = (sectionConfig: any) => {
    const newSection = {
      id: `${sectionConfig.id}-${Date.now()}`,
      type: sectionConfig.type,
      layout: sectionConfig.layouts?.[0] || "default",
      title: sectionConfig.title,
      order: proposal.sections.length + 1,
      // Deep copy so two sections of the same type never share a list.
      content: JSON.parse(JSON.stringify(sectionConfig.defaultContent || {})),
      // The BD added this one by hand — the review screen labels it as such.
      origin: "manual",
      approved: false,
    };
    updateProposal({ sections: [...proposal.sections, newSection] });
    setActiveSection(newSection.id);
    setShowAddModal(false);
  };

  const moveSection = (sectionId: string, direction: "up" | "down") => {
    const index = proposal.sections.findIndex((s: any) => s.id === sectionId);
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= proposal.sections.length) return;
    const newSections = arrayMove(proposal.sections, index, newIndex).map(
      (s: any, i: number) => ({ ...s, order: i + 1 })
    );
    updateProposal({ sections: newSections });
  };

  const toggleSectionVisibility = (sectionId: string) => {
    const newSections = proposal.sections.map((s: any) =>
      s.id === sectionId ? { ...s, hidden: !s.hidden } : s
    );
    updateProposal({ sections: newSections });
  };

  const currentSection = proposal.sections.find((s: any) => s.id === activeSection);

  return (
    <div className="h-[calc(100vh-80px)] flex bg-gray-100">

      <div className="w-72 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-800">Sections</h3>
            <button
              onClick={() => setShowAddModal(true)}
              className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-500">Drag to reorder • Click to edit</p>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={proposal.sections.map((s: any) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {proposal.sections
                  .sort((a: any, b: any) => a.order - b.order)
                  .map((section: any) => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      isActive={activeSection === section.id}
                      onClick={() => setActiveSection(section.id)}
                      onDelete={() => deleteSection(section.id)}
                      onDuplicate={() => duplicateSection(section.id)}
                      onToggleVisibility={() => toggleSectionVisibility(section.id)}
                      onMoveUp={() => moveSection(section.id, "up")}
                      onMoveDown={() => moveSection(section.id, "down")}
                    />
                  ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {draggedSection && (
                <div className="bg-white rounded-lg p-3 shadow-xl border-2 border-emerald-500 opacity-90">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-sm">{draggedSection.title}</span>
                  </div>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        </div>

        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-emerald-500 hover:text-emerald-500 hover:bg-emerald-50 transition-all"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">Add Section</span>
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col">

        <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-800 text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
            <div className="h-6 w-px bg-gray-200 mx-2" />
            <button
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              className={`p-2 rounded-lg transition-colors ${
                historyIndex > 0 ? "text-gray-600 hover:bg-gray-100" : "text-gray-300 cursor-not-allowed"
              }`}
              title="Undo"
            >
              <Undo className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              className={`p-2 rounded-lg transition-colors ${
                historyIndex < history.length - 1 ? "text-gray-600 hover:bg-gray-100" : "text-gray-300 cursor-not-allowed"
              }`}
              title="Redo"
            >
              <Redo className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setPreviewMode("desktop")}
                className={`p-2 rounded-md transition-colors ${previewMode === "desktop" ? "bg-white shadow-sm" : ""}`}
              >
                <Monitor className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPreviewMode("mobile")}
                className={`p-2 rounded-md transition-colors ${previewMode === "mobile" ? "bg-white shadow-sm" : ""}`}
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>

            <button
              onClick={() => onSave(proposal)}
              disabled={isSaving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save &amp; Review
                </>
              )}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">

          <div className="p-6 bg-white border-b border-gray-200">
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">
              Proposal Settings
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name</label>
                <input
                  type="text"
                  value={proposal.fullName || ""}
                  onChange={(e) => updateProposal({ fullName: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                  placeholder="Irfan Baig"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Tagline / Headline</label>
                <input
                  type="text"
                  value={proposal.tagline || ""}
                  onChange={(e) => updateProposal({ tagline: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                  placeholder="Full-Stack Developer | React & Node Expert"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Profile Image URL</label>
                <input
                  type="text"
                  value={proposal.profileImage || ""}
                  onChange={(e) => updateProposal({ profileImage: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Proposal Title</label>
                <input
                  type="text"
                  value={proposal.title || ""}
                  onChange={(e) => updateProposal({ title: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                  placeholder="Enterprise Digital Proposal"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Upwork Account</label>
                <select
                  value={(typeof proposal.upworkAccount === "object" ? proposal.upworkAccount?._id : proposal.upworkAccount) || ""}
                  onChange={(e) => updateProposal({ upworkAccount: e.target.value || null })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none bg-white"
                >
                  <option value="">— None —</option>
                  {accounts.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Client / Contact</label>
                <select
                  value={(typeof proposal.client === "object" ? proposal.client?._id : proposal.client) || ""}
                  onChange={(e) => updateProposal({ client: e.target.value || null })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none bg-white"
                >
                  <option value="">— None —</option>
                  {clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Hire Me Link (Upwork / profile)</label>
                <input
                  type="text"
                  value={proposal.hireLink || ""}
                  onChange={(e) => updateProposal({ hireLink: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-emerald-500 outline-none"
                  placeholder="https://www.upwork.com/freelancers/..."
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-2">
                Stats ({(proposal.stats || []).length})
              </label>
              <div className="flex gap-3 flex-wrap">
                {(proposal.stats || []).map((stat: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                    <input
                      type="text"
                      value={stat.value}
                      onChange={(e) => {
                        const newStats = [...(proposal.stats || [])];
                        newStats[i] = { ...newStats[i], value: e.target.value };
                        updateProposal({ stats: newStats });
                      }}
                      className="w-16 text-center font-bold text-sm border border-gray-200 rounded px-1 py-0.5 focus:border-emerald-500 outline-none"
                    />
                    <input
                      type="text"
                      value={stat.label}
                      onChange={(e) => {
                        const newStats = [...(proposal.stats || [])];
                        newStats[i] = { ...newStats[i], label: e.target.value };
                        updateProposal({ stats: newStats });
                      }}
                      className="w-28 text-xs border border-gray-200 rounded px-1 py-0.5 focus:border-emerald-500 outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {currentSection ? (
            <>
              {(enhancingId === currentSection.id || revertMap[currentSection.id]) && (
                <div className="mb-3 flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-violet-50 border border-violet-200 text-[12px]">
                  {enhancingId === currentSection.id ? (
                    <span className="font-semibold text-violet-700">Enhancing this section with AI…</span>
                  ) : (
                    <>
                      <span className="font-semibold text-violet-700">✨ AI enhanced — your original was saved.</span>
                      <span className="flex items-center gap-3">
                        <button
                          onClick={() => setCompare({
                            sectionId: currentSection.id,
                            before: revertMap[currentSection.id],
                            after: { title: currentSection.title, subtitle: currentSection.subtitle, content: currentSection.content },
                          })}
                          className="inline-flex items-center gap-1 font-bold text-violet-700 hover:underline"
                        >
                          <Eye size={12} /> Compare before / after
                        </button>
                        <button onClick={() => revertSection(currentSection.id)} className="inline-flex items-center gap-1 font-bold text-violet-700 hover:underline">
                          <RotateCcw size={12} /> Revert to original
                        </button>
                      </span>
                    </>
                  )}
                </div>
              )}
              <SectionEditor
                section={currentSection}
                templateConfig={templateConfig}
                onUpdate={(updates) => updateSection(currentSection.id, updates)}
                onRegenerateAI={(sectionId) => enhanceSection(sectionId)}
              />
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Plus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No section selected</p>
                <p className="text-sm">Click a section to edit or add a new one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-[600px] bg-gray-50 border-l border-gray-200 overflow-hidden flex flex-col">
        <div className="p-4 bg-white border-b border-gray-200">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Live Preview
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div
            className={`bg-white rounded-lg shadow-sm overflow-hidden transform origin-top transition-all ${
              previewMode === "mobile" ? "max-w-[595px]" : "max-w-[995px]"
            }`}
          >
            <LivePreview
              proposal={proposal}
              templateConfig={templateConfig}
              scale={0.5}
            />
          </div>
        </div>
      </div>

      {showAddModal && (
        <AddSectionModal
          onClose={() => setShowAddModal(false)}
          onAdd={addSection}
          templateId={templateId}
          existingSections={proposal.sections.map((s: any) => s.id)}
        />
      )}

      {/* Before / After AI comparison — BD Input vs AI Enhanced, side by side */}
      {compare && compare.before && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setCompare(null)}>
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-bold text-lg">Before &amp; After AI Enhancement</h3>
              <button onClick={() => setCompare(null)} className="p-2 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto grid grid-cols-2 divide-x divide-gray-200">
              {[
                { label: "Your Input (BD)", data: compare.before, cls: "bg-gray-50" },
                { label: "AI Enhanced", data: compare.after, cls: "bg-violet-50/40" },
              ].map(({ label, data, cls }) => (
                <div key={label} className={`p-5 ${cls}`}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">{label}</p>
                  {data?.title ? (
                    <>
                      <p className="text-[10px] font-bold uppercase text-gray-400">Title</p>
                      <p className="text-sm font-semibold text-gray-800 mb-3 whitespace-pre-wrap">{data.title}</p>
                    </>
                  ) : null}
                  {data?.subtitle ? (
                    <>
                      <p className="text-[10px] font-bold uppercase text-gray-400">Subtitle</p>
                      <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{data.subtitle}</p>
                    </>
                  ) : null}
                  <p className="text-[10px] font-bold uppercase text-gray-400">Content</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{flatText(data?.content)}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-white">
              <button
                onClick={() => { revertSection(compare.sectionId); setCompare(null); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <RotateCcw className="w-4 h-4" /> Revert to Original
              </button>
              <button
                onClick={() => setCompare(null)}
                className="px-5 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700"
              >
                Keep AI Version — continue editing
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="font-bold text-lg">Full Preview</h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => window.open(`/proposal/preview/${proposal._id}`, "_blank")}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open in New Tab
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-100">
              <LivePreview
                proposal={proposal}
                templateConfig={templateConfig}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalBuilderEditor;
